/**
 * Web twin of `CreateCvTailorStep` - step 2: WHAT the CV gets tailored to.
 *
 * Reached after step 1 has answered where the content comes from, so it opens
 * with a recap of that answer. A CV skill or job description is optional; if
 * neither is selected, the primary action creates an unchanged raw copy.
 *
 * The job-description editor is rendered ONCE, below the options. It used to be
 * built twice - nested in the skill card and again in the JD card - which put
 * two editors for the same text on one screen.
 */
import { useRef } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { SkillInputField, SkillSummary } from '@tabbio/types';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { useLocale } from '../../../hooks/useLocale';
import { Alert } from '../../../lib/alert.web';
import { SkillDeclaredInputs } from '../../skills/skill-ui.web';
import { CreateCvOptionCard } from './CreateCvOptionCard';
import { CvSkillPicker } from './CvSkillPicker';
import type { CreateCvSource, CreateCvTailorMode } from './create-cv-machine';
import {
  JOB_POST_ATTACHMENT_ACCEPT,
  useJobPostAttachment,
} from './use-job-post-attachment';
import {
  ATTACH_ROW,
  ATTACH_SPINNER,
  FIELD_LABEL,
  RECAP,
  TEXTAREA,
} from './create-cv-web-styles';

interface Props {
  source: CreateCvSource | null;
  /** Human-readable recap of step 1, shown so the user keeps their bearings. */
  sourceSummary: string;
  tailorMode: CreateCvTailorMode;
  onTailorModeChange: (mode: CreateCvTailorMode) => void;
  selectedSkillId: string | null;
  onSkillChange: (skill: SkillSummary) => void;
  jobDescription: string;
  onJobDescriptionChange: (value: string) => void;
  /** True when the picked skill reads a job description as well as the CV. */
  needsJobDescription: boolean;
  /**
   * The picked skill's own run-screen questions. A skill that declares a
   * required one (Relocate to Gulf asks which market) used to fail server-side
   * on every press, because this step had nowhere to ask it.
   */
  skillInputFields: SkillInputField[] | null;
  skillFieldValues: Record<string, string>;
  onSkillFieldChange: (id: string, value: string) => void;
  skillDetailLoading: boolean;
}

export function CreateCvTailorStep({
  source,
  sourceSummary,
  tailorMode,
  onTailorModeChange,
  selectedSkillId,
  onSkillChange,
  jobDescription,
  onJobDescriptionChange,
  needsJobDescription,
  skillInputFields,
  skillFieldValues,
  onSkillFieldChange,
  skillDetailLoading,
}: Props) {
  const { t } = useTranslation('seeker');
  const { isRTL } = useLocale();
  const jobPostInputRef = useRef<HTMLInputElement>(null);

  const attachment = useJobPostAttachment({
    jobDescription,
    onJobDescriptionChange,
    onError: (title, message) => Alert.alert(title, message),
  });

  const handleJobPostFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    event.currentTarget.value = '';
    if (!selected) return;
    void attachment.attach({
      name: selected.name,
      type: selected.type || 'application/octet-stream',
      file: selected,
      size: selected.size,
    });
  };

  const showJobDescription = tailorMode === 'jd' || needsJobDescription;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {sourceSummary ? <div style={RECAP}>{sourceSummary}</div> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <CreateCvOptionCard
          icon={<MaterialSymbol name="layers" size={20} />}
          title={t('cv.create.tailor.skill')}
          subtitle={t('cv.create.tailor.skillHint')}
          selected={tailorMode === 'skill'}
          onPress={() => onTailorModeChange('skill')}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <CvSkillPicker
              source={source}
              selectedSkillId={selectedSkillId}
              onSelect={onSkillChange}
            />
            {/* The skill's own questions. Everything it needs is asked here, so
                there is no second place to type the same job post. */}
            {skillInputFields ? (
              <SkillDeclaredInputs
                fields={skillInputFields}
                values={skillFieldValues}
                onChange={onSkillFieldChange}
                isRTL={isRTL}
              />
            ) : skillDetailLoading && selectedSkillId ? (
              <span aria-hidden style={ATTACH_SPINNER} />
            ) : null}
          </div>
        </CreateCvOptionCard>

        <CreateCvOptionCard
          icon={<MaterialSymbol name="work" size={20} />}
          title={t('cv.create.tailor.jd')}
          subtitle={t('cv.create.tailor.jdHint')}
          selected={tailorMode === 'jd'}
          onPress={() => onTailorModeChange('jd')}
        />
      </div>

      {showJobDescription ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {needsJobDescription ? (
            <span style={FIELD_LABEL}>
              {t('cv.create.tailor.skillNeedsJd')}
            </span>
          ) : null}
          <textarea
            value={jobDescription}
            onChange={(e) => onJobDescriptionChange(e.target.value)}
            placeholder={t('cv.create.tailor.jdPlaceholder')}
            rows={7}
            style={TEXTAREA}
            dir="auto"
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              type="button"
              onClick={() => {
                if (!attachment.isExtracting) jobPostInputRef.current?.click();
              }}
              disabled={attachment.isExtracting}
              aria-label={t('cv.create.tailor.jdAttach')}
              style={{
                ...ATTACH_ROW,
                cursor: attachment.isExtracting ? 'default' : 'pointer',
              }}
            >
              {attachment.isExtracting ? (
                <span aria-hidden style={ATTACH_SPINNER} />
              ) : (
                <MaterialSymbol
                  name="attach_file"
                  size={16}
                  style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
                />
              )}
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {attachment.fileName ?? t('cv.create.tailor.jdAttach')}
              </span>
            </button>
            {attachment.fileName && !attachment.isExtracting ? (
              <button
                type="button"
                onClick={attachment.clear}
                aria-label={t('cv.create.tailor.jdAttachClear')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 2,
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: 'var(--text-tertiary)',
                }}
              >
                <MaterialSymbol name="close" size={14} />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <input
        ref={jobPostInputRef}
        type="file"
        accept={JOB_POST_ATTACHMENT_ACCEPT}
        onChange={handleJobPostFileChange}
        style={{ display: 'none' }}
      />
    </div>
  );
}
